import { loadScript, pushTrack, waitWithTimeout } from "utils";
import { AdEnginePlugin, type Hooks } from "../../core/AdEnginePlugin";
import type { InstantConfigRepository } from "instant-config";
import type { IntentIqObject } from "./types";
import type { GptWrapper } from "../../core/GptWrapper";
import { PrebidPlugin } from "../PrebidPlugin/PrebidPlugin";
import {
  bidToReport,
  getIntentIqObjectInstance,
  getIntentIqScriptUrl,
  waitUntilIntentIqObjectIsAvailable,
} from "./helpers";
import { type FandomCmp, LoadTimeEvent } from "common-types";
import invariant from "tiny-invariant";
import { isConsentGranted } from "../../helpers/isConsentGranted";
import { isChromeBrowser } from "../../helpers/isBrowser";

const LIBRARY_LOAD_MS = 1000;

export type IntentIqPluginConfig = Pick<
  InstantConfigRepository,
  "icPrebidIntentIQ" | "icPrebidIntentIQVersion" | "icPrebidIntentIQTimeout"
>;

/**
 * Intent IQ plugin for AdEngine.
 *
 * - Requires consent
 * - It's installed manually (separately) and not as Prebid module.
 * - It is used to report on the Prebid wins to Intent IQ.
 *
 * @see https://docs.prebid.org/dev-docs/modules/userid-submodules/intentiq.html
 * @see https://pbmodule.documents.intentiq.com/ (requires login)
 * @see https://docs.prebid.org/dev-docs/analytics/intentiq.html
 */
export class IntentIqPlugin extends AdEnginePlugin {
  private consent = false;
  private enabled = false;
  private intentIqObject?: IntentIqObject;
  private gptWrapper?: GptWrapper;
  private pbjs?: Prebid;

  constructor(private readonly config: IntentIqPluginConfig) {
    super();
  }

  static isEnabled(config: IntentIqPluginConfig): boolean {
    return (
      !isChromeBrowser() &&
      config.icPrebidIntentIQ &&
      !!config.icPrebidIntentIQVersion &&
      !!config.icPrebidIntentIQTimeout
    );
  }

  private async initialize(pbjs: Prebid): Promise<void> {
    if (this.enabled || !this.consent) {
      return;
    }

    await this.loadIntentIqLibrary(pbjs);

    const adRenderSucceededHandler: OnAdRenderSucceededHandler = ({ bid }) => {
      this.reportPrebidWin(bid);
    };
    pbjs.onEvent("adRenderSucceeded", adRenderSucceededHandler);
  }

  private async loadIntentIqLibrary(pbjs: Prebid): Promise<void> {
    // Initialize the plugin
    try {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "intentiq_started",
        time: performance.now(),
      });
      // preload script
      await waitWithTimeout(
        loadScript(getIntentIqScriptUrl(this.config.icPrebidIntentIQVersion)),
        LIBRARY_LOAD_MS,
      );

      // wait until the script is executed and does its thing
      await waitUntilIntentIqObjectIsAvailable(
        this.config.icPrebidIntentIQVersion,
      );

      await getIntentIqObjectInstance(
        this.config.icPrebidIntentIQVersion,
        this.config.icPrebidIntentIQTimeout,
        pbjs,
        (intentIqObject, targeting) => {
          this.enabled = true;
          this.intentIqObject = intentIqObject;
          this.setTargeting(targeting);
          pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
            eventName: "intentiq_loaded",
            time: performance.now(),
          });
        },
      );
    } catch (e) {
      pushTrack(LoadTimeEvent.VENDOR_TIME_UPDATE, {
        eventName: "intentiq_error_loading",
        time: performance.now(),
      });
      console.error("Failed to load Intent IQ script", e);
    }
  }

  private setTargeting(abTestGroup: string) {
    invariant(this.gptWrapper, "GPT wrapper is not available");
    if (abTestGroup && this.gptWrapper) {
      this.gptWrapper.setTargeting("intent_iq_group", abTestGroup);
    }
  }

  private reportPrebidWin(bid: PrebidBidResponse) {
    if (!this.enabled || !this.intentIqObject) {
      return;
    }

    const win = bidToReport(bid);
    this.intentIqObject.reportExternalWin(win);
  }

  hooks: Hooks = {
    onBiddersInit: () => {
      this.usePlugin(PrebidPlugin)?.addPrebidReadyCallback((pbjs: Prebid) => {
        this.pbjs = pbjs;

        this.initialize(this.pbjs);
      });
    },

    onConsentUpdated: (fandomCmp: FandomCmp) => {
      this.consent = isConsentGranted(fandomCmp);
      if (!this.pbjs) {
        return;
      }

      this.initialize(this.pbjs);
    },

    onAfterGptWrapperInit: (gpt: GptWrapper) => {
      this.gptWrapper = gpt;
    },
  };
}
